'use client';

import { useEffect, useMemo, useState } from 'react';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import { UserSettings } from 'src/environmentVariables/EnvironmentVariables';
import InterfaceSettings from 'src/components/Settings/InterfaceSettings/InterfaceSettings';
import MarginSettings, {
	AllSubaccountUpdates,
} from './MarginSettings/MarginSettings';
import NetworkSettings from 'src/components/Settings/NetworkSettings/NetworkSettings';
import OtherSettings from 'src/components/Settings/OtherSettings';
import TradeSettings from 'src/components/Settings/TradeSettings/TradeSettings';
import { useSwapClient } from 'src/hooks/velocityActions/swaps/useSwapClient';
import { useImmer } from 'use-immer';
import { handleRefresh } from 'src/utils/dom';
import useCurrentSettings from 'src/hooks/useCurrentSettings';
import { SlippageTolerance } from 'src/@types/types';
import { SettingsModalView } from 'src/components/Modals/SettingsModal/types';
import Button from 'src/components/primitives/Button/Button';
import LayoutSettings from 'src/components/Settings/LayoutSettings';
import { NOTIFICATION_CONFIGURATIONS } from 'src/constants/constants';
import { GridConfigValue } from 'src/hooks/useTradePageLayout/type';
import useNotificationPlacementSetting from 'src/hooks/useNotificationPlacementSetting';
import { omit } from 'lodash';
import useLayoutConfigSetting from 'src/hooks/useLayoutConfigSetting';
import BaseModal from 'src/components/primitives/BaseModal/BaseModal';
import useUpdateSubaccountSettings from 'src/hooks/velocityActions/user/useUpdateSubaccountSettings';

interface SettingsFormProps {
	onClose: () => void;
	currentView: SettingsModalView;
}

export type Settings = UserSettings & {
	layoutSetting2: GridConfigValue;
	notificationPlacement: (typeof NOTIFICATION_CONFIGURATIONS)[number]['value'];
};

const SettingsForm = ({ onClose, currentView }: SettingsFormProps) => {
	const setState = useVelocityStore((s) => s.set);

	const [savedSettings, updateSettings] = useCurrentSettings();
	const [layoutConfig, setLayoutConfig] = useLayoutConfigSetting();
	const [notificationPlacement, setNotificationPlacement] =
		useNotificationPlacementSetting();

	const [stagedSettings, setStagedSettings] = useImmer<Settings>({
		...savedSettings,
		theme: savedSettings.theme ?? 'dark',
		layoutSetting2: layoutConfig || 'default',
		notificationPlacement,
	});

	const [disableSave, setDisableSave] = useState(false);
	const toggleSetting = (key: keyof UserSettings) => {
		setStagedSettings({ ...stagedSettings, [key]: !stagedSettings[key] });
	};

	const [isRpcDropdownOpen, setIsRpcDropdownOpen] = useState(false);
	const toggleRpcDropdown = () => {
		setIsRpcDropdownOpen(!isRpcDropdownOpen);
	};

	const [marginTradingUpdates, setMarginTradingUpdates] = useState<
		{
			marginTradingEnabled: boolean;
			subAccountId: number;
		}[]
	>([]);

	const [marginRatioUpdates, setMarginRatioUpdates] = useState<
		{ maxLeverage: number | undefined; subAccountId: number }[]
	>([]);

	const [reduceOnlyFlagUpdates, setReduceOnlyFlagUpdates] = useState<
		{ reduceOnly: boolean; subAccountId: number }[]
	>([]);

	const { updateSwapClient } = useSwapClient();

	/* Txn requiring settings reset if you switch to another tab, to not confuse users */
	useEffect(() => {
		if (currentView !== 'margin') {
			setMarginRatioUpdates([]);
			setMarginTradingUpdates([]);
		}
	}, [currentView]);

	// this syncs staged settings when saved settings are changed outside of this component
	useEffect(() => {
		setStagedSettings({
			...savedSettings,
			layoutSetting2: layoutConfig || 'default',
			notificationPlacement,
		});
	}, [savedSettings, layoutConfig, notificationPlacement, setStagedSettings]);

	const isInputChanged = useMemo(() => {
		const nonMarginSettingsChanged = Object.keys(stagedSettings).some((key) => {
			if (key === 'rpcEndpoint') {
				return (
					stagedSettings.rpcEndpoint.value !== savedSettings.rpcEndpoint.value
				);
			}

			if (key === 'layoutSetting2') {
				return stagedSettings.layoutSetting2 !== layoutConfig;
			}

			if (key === 'notificationPlacement') {
				return stagedSettings.notificationPlacement !== notificationPlacement;
			}

			return (
				stagedSettings[key as keyof UserSettings] !==
				savedSettings[key as keyof UserSettings]
			);
		});

		const marginSettingsChanged =
			marginTradingUpdates.length ||
			marginRatioUpdates.length ||
			reduceOnlyFlagUpdates.length;

		return nonMarginSettingsChanged || marginSettingsChanged;
	}, [
		marginTradingUpdates.length,
		marginRatioUpdates.length,
		reduceOnlyFlagUpdates.length,
		stagedSettings,
		layoutConfig,
		notificationPlacement,
		savedSettings,
	]);

	const allSubAccountUpdates: AllSubaccountUpdates = useMemo(() => {
		const subAccountUpdates: AllSubaccountUpdates = {};

		marginTradingUpdates.forEach((update) => {
			subAccountUpdates[update.subAccountId] = {
				...subAccountUpdates[update.subAccountId],
				marginTradingEnabled: update.marginTradingEnabled,
			};
		});

		marginRatioUpdates.forEach((update) => {
			subAccountUpdates[update.subAccountId] = {
				...subAccountUpdates[update.subAccountId],
				maxLeverage: update.maxLeverage,
			};
		});

		reduceOnlyFlagUpdates.forEach((update) => {
			subAccountUpdates[update.subAccountId] = {
				...subAccountUpdates[update.subAccountId],
				reduceOnly: update.reduceOnly,
			};
		});

		return subAccountUpdates;
	}, [marginTradingUpdates, marginRatioUpdates, reduceOnlyFlagUpdates]);

	const inputValid = useMemo(() => {
		// if changing priority fee to custom, must be a value there
		return (
			stagedSettings.priorityFee !== 'custom' ||
			(stagedSettings.priorityFee === 'custom' &&
				stagedSettings.customPriorityFee)
		);
	}, [stagedSettings]);

	const addToMarginRatioUpdates = (
		subAccountId: number,
		maxLeverage: number | undefined
	) => {
		const indexToReplace = marginRatioUpdates.findIndex(
			(val) => val.subAccountId === subAccountId
		);

		if (indexToReplace > -1) {
			// -1 passed tells us that it's the same as original and to remove from the changing array
			if (maxLeverage === -1) {
				setMarginRatioUpdates(
					marginRatioUpdates.filter(
						(item) => item.subAccountId !== subAccountId
					)
				);
			} else {
				const updatesCopy = [...marginRatioUpdates];
				updatesCopy[indexToReplace].maxLeverage = maxLeverage;
				setMarginRatioUpdates(updatesCopy);
			}
		} else {
			setMarginRatioUpdates(
				marginRatioUpdates.concat({
					maxLeverage: maxLeverage,
					subAccountId,
				})
			);
		}
	};

	const addToMarginTradingUpdates = (
		subAccountId: number,
		marginTradingEnabled: boolean
	) => {
		const indexToReplace = marginTradingUpdates.findIndex(
			(val) => val.subAccountId === subAccountId
		);

		if (indexToReplace > -1) {
			// undefined passed tells us that it's the same as original and to remove from the changing array
			if (marginTradingEnabled == undefined) {
				setMarginTradingUpdates(
					marginTradingUpdates.filter(
						(item) => item.subAccountId !== subAccountId
					)
				);
			} else {
				const updatesCopy = [...marginTradingUpdates];
				updatesCopy[indexToReplace].marginTradingEnabled = marginTradingEnabled;
				setMarginTradingUpdates(updatesCopy);
			}
		} else {
			setMarginTradingUpdates(
				marginTradingUpdates.concat({ marginTradingEnabled, subAccountId })
			);
		}
	};

	const addToReduceOnlyUpdates = (
		subAccountId: number,
		reduceOnly: boolean
	) => {
		const indexToReplace = reduceOnlyFlagUpdates.findIndex(
			(val) => val.subAccountId === subAccountId
		);

		if (indexToReplace > -1) {
			// undefined passed tells us that it's the same as original and to remove from the changing array
			if (reduceOnly == undefined) {
				setReduceOnlyFlagUpdates(
					reduceOnlyFlagUpdates.filter(
						(item) => item.subAccountId !== subAccountId
					)
				);
			} else {
				const updatesCopy = [...reduceOnlyFlagUpdates];
				updatesCopy[indexToReplace].reduceOnly = reduceOnly;
				setReduceOnlyFlagUpdates(updatesCopy);
			}
		} else {
			setReduceOnlyFlagUpdates(
				reduceOnlyFlagUpdates.concat({ reduceOnly, subAccountId })
			);
		}
	};

	const { updateSubaccountSettings } = useUpdateSubaccountSettings();
	const handleUpdateAllSubaccountSettings = async () => {
		await updateSubaccountSettings(allSubAccountUpdates);
	};

	const handleSave = async () => {
		setDisableSave(true);

		updateSettings(
			omit(stagedSettings, ['layoutSetting2', 'notificationPlacement'])
		);

		setLayoutConfig(stagedSettings.layoutSetting2);
		setNotificationPlacement(stagedSettings.notificationPlacement);

		// TODO: Is this stuff necessary?
		const isEnvChanged =
			stagedSettings.devnetOverride !== savedSettings.devnetOverride;
		const isRpcChanged =
			stagedSettings.rpcEndpoint.value !== savedSettings.rpcEndpoint.value;
		const isStagingDlobServerChanged =
			stagedSettings.stagingDlobServerOverride !==
			savedSettings.stagingDlobServerOverride;
		const isStagingDataServerChanged =
			stagedSettings.stagingDataServerOverride !==
			savedSettings.stagingDataServerOverride;
		const isSubscriberTypeChanged =
			stagedSettings.accountSubscriberType !==
			savedSettings.accountSubscriberType;
		const isSwapClientTypeChanged =
			stagedSettings.swapClientType !== savedSettings.swapClientType;

		setState((s) => {
			s.tradeForm.slippageTolerance =
				stagedSettings.slippageTolerance as SlippageTolerance;
		});

		// Update swap client if the type changed
		if (isSwapClientTypeChanged) {
			updateSwapClient();
		}

		/* TX REQUIRING UPDATES */
		if (
			marginRatioUpdates.length ||
			marginTradingUpdates.length ||
			reduceOnlyFlagUpdates.length
		) {
			await handleUpdateAllSubaccountSettings();
		}

		if (isEnvChanged) {
			handleRefresh('About to refresh to change Environment');
		} else if (isRpcChanged) {
			handleRefresh('About to refresh to change RPC Node');
		} else if (isStagingDlobServerChanged) {
			handleRefresh('About to refresh to change dlob server');
		} else if (isStagingDataServerChanged) {
			handleRefresh('About to refresh to change data server');
		} else if (isSubscriberTypeChanged) {
			handleRefresh(`About to refresh to toggle websockets`);
		}

		setDisableSave(false);

		onClose();
	};

	return (
		<>
			<BaseModal.Body>
				{currentView === 'trade' && (
					<TradeSettings
						localSettings={stagedSettings}
						setLocalSettings={setStagedSettings}
					/>
				)}
				{currentView === 'network' && (
					<NetworkSettings
						localSettings={stagedSettings}
						setLocalSettings={setStagedSettings}
						toggleRpcDropdown={toggleRpcDropdown}
						isRpcDropdownOpen={isRpcDropdownOpen}
					/>
				)}
				{currentView === 'layout' && (
					<LayoutSettings
						localSettings={stagedSettings}
						setLocalSettings={setStagedSettings}
					/>
				)}
				{currentView === 'interface' && (
					<InterfaceSettings
						localSettings={stagedSettings}
						setLocalSettings={setStagedSettings}
					/>
				)}
				{currentView === 'margin' && (
					<MarginSettings
						allSubAccountUpdates={allSubAccountUpdates}
						addToMarginRatioUpdates={addToMarginRatioUpdates}
						addToMarginTradingUpdates={addToMarginTradingUpdates}
						addToReduceOnlyUpdates={addToReduceOnlyUpdates}
					/>
				)}
				{currentView === 'other' && (
					<OtherSettings
						localSettings={stagedSettings}
						toggleSetting={toggleSetting}
						updateSettings={setStagedSettings}
					/>
				)}
			</BaseModal.Body>
			<BaseModal.Footer className="sm:justify-end">
				<Button
					className="flex-1 sm:w-[80px] sm:flex-none"
					variant="secondary"
					size="lg"
					onClick={onClose}
					disabled={disableSave}
				>
					Cancel
				</Button>
				<Button
					className="flex-1 sm:w-[80px] sm:flex-none"
					size="lg"
					onClick={handleSave}
					disabled={!isInputChanged || !inputValid || disableSave}
				>
					Save
				</Button>
			</BaseModal.Footer>
		</>
	);
};

export default SettingsForm;
